// src/components/WelcomePage.js
import React from 'react';
import { User, Link as LinkIcon, Image, Crown, Palette } from 'lucide-react';
import styles from './WelcomePage.module.css';

// Define the stripe payment link - same as in Dashboard.js
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/dR614x7iH0E44366oo';

const WelcomePage = ({ userData, setActiveSection, isProUser }) => {
  if (!userData || !userData.profile) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  // Get the user's icons and links count
  const iconsCount = userData.profile.icons ? userData.profile.icons.length : 0;
  const linksCount = userData.profile.links ? userData.profile.links.length : 0;
  
  // Get the palette for styling
  const palette = userData.profile.palette || { 
    colors: ['#f1f1c0', '#ccacac']  // Default palette if none exists
  };
  
  // Use the first color as background base
  const primaryColor = palette.colors && palette.colors.length > 0 
    ? palette.colors[0] 
    : '#f1f1c0';
  
  // Use the second color as accent if available
  const accentColor = palette.colors && palette.colors.length > 1 
    ? palette.colors[1] 
    : '#ccacac';
  
  // Create gradient background style
  const backgroundStyle = {
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`
  };

  // Get all available colors from the palette (up to 5)
  const paletteColors = palette.colors || [];
  const displayColors = paletteColors.slice(0, 5); // Limit to 5 colors
  
  // Fill with placeholder colors if less than 5
  while (displayColors.length < 5) {
    displayColors.push('#e0e0e0');
  }

  // Handle navigation to different sections
  const handleNavigate = (section) => {
    if (typeof setActiveSection === 'function') {
      setActiveSection(section);
    } else {
      console.warn('setActiveSection is not a function or wasn\'t passed to WelcomePage');
    }
  };

  // Handle palette click to navigate to palette section
  const handlePaletteClick = () => {
    setActiveSection('palette');
  };

  // Handle upgrade click
  const handleUpgrade = () => {
    // Store user ID in local storage if needed
    if (userData && userData.uid) {
      localStorage.setItem('upgradeUserId', userData.uid);
    }
    // Redirect to payment page
    window.location.href = STRIPE_PAYMENT_LINK;
  };

  return (
    <div className={styles.welcomeContainer} style={backgroundStyle}>
      <div className={styles.contentCard}>
        <div className={styles.profileImageContainer}>
          {userData.photoURL ? (
            <img 
              src={userData.photoURL} 
              alt="Profile" 
              className={styles.profileImage}
              onError={(e) => {
                e.target.src = '';
              }}
            />
          ) : (
            <div className={styles.initialsBadge}>
              {userData.profile?.displayName
                ? userData.profile.displayName.charAt(0).toUpperCase()
                : (userData.username ? userData.username.charAt(0).toUpperCase() : 'U')
              }
            </div>
          )}
        </div>

        <h1 className={styles.welcomeHeading}>
          Welcome, {userData.profile.displayName || userData.username}!
        </h1>
        
        {/* Palette display */}
        <div className={styles.paletteDisplay} onClick={handlePaletteClick}>
          <div className={styles.paletteIcon}>
            <Palette size={16} />
          </div>
          <div className={styles.paletteColors}>
            {displayColors.map((color, index) => (
              <div 
                key={index} 
                className={styles.paletteColor}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <span className={styles.paletteName}>
            {palette.name || 'Custom Palette'}
          </span>
        </div>
        
        <p className={styles.welcomeText}>
          Choose a menu item to customize your profile
        </p>
        
        <div className={styles.statsContainer}>
          <div className={styles.statCard} onClick={() => handleNavigate('details')}>
            <div className={styles.statIcon}>
              <User size={24} />
            </div>
            <div className={styles.statInfo}>
              <h3>Profile</h3>
              <p>{userData.profile.bio ? 'Bio added' : 'Add a bio'}</p>
            </div>
          </div>
          
          <div className={styles.statCard} onClick={() => handleNavigate('links')}>
            <div className={styles.statIcon}>
              <LinkIcon size={24} />
            </div>
            <div className={styles.statInfo}>
              <h3>Links</h3>
              <p>{linksCount} active link{linksCount !== 1 ? 's' : ''}</p>
            </div>
          </div>
          
          <div className={styles.statCard} onClick={() => handleNavigate('icons')}>
            <div className={styles.statIcon}>
              <Image size={24} />
            </div>
            <div className={styles.statInfo}>
              <h3>Icons</h3>
              <p>{iconsCount} social icon{iconsCount !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>

        {/* Pro upgrade card - only shown to free users */}
        {!isProUser && (
          <div className={styles.proUpgradeCard}>
            <div className={styles.proCardContent}>
              <div className={styles.proIconWrapper}>
                <Crown size={28} className={styles.proCrown} />
              </div>
              <div className={styles.proTextContent}>
                <h3>Upgrade to Pro</h3>
                <p>Get exclusive features like Ghost Mode, Link Shortener, and Premium Themes</p>
              </div>
              <button 
                className={styles.upgradeButton} 
                onClick={handleUpgrade}
              >
                Upgrade
              </button>
            </div>
          </div>
        )}

        {userData.profile.lastPublished ? (
          <div className={styles.lastPublished}>
            <p>Last published: {new Date(userData.profile.lastPublished).toLocaleDateString()}</p>
          </div>
        ) : (
          <div className={styles.publishTip}>
            <p>Tip: Don't forget to publish your changes when you're done editing!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePage;